import React from 'react';
import { DatePicker, Dropdown, IDropdownOption, SearchBox, Stack } from '@fluentui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCalendarCheck, 
    faCalendarDays, 
    faClock, 
    faExclamationTriangle, 
    faList, 
    faSpinner, 
    faTableColumns,
    faTruckFast
} from '@fortawesome/pro-light-svg-icons';
import moment from 'moment';
import styles from '../styles.module.scss';
import { TOperationStatusFilter, TOperationTypeFilter, TOperationViewMode } from '../types';
import GeneralService from '../../../../services/general';
import Label from '../../../typography/label';
import Text from '../../../typography/text';

interface IWorkspaceHeaderProps {
    counts: {
        total: number;
        overdue: number;
        inProgress: number;
        completed: number;
    };
    selectedDate: {
        startDate: string;
        endDate: string;
    };
    onDateChange(startDate: string, endDate: string): void;
    
    statusFilter: TOperationStatusFilter;
    onStatusFilterChange(filter: TOperationStatusFilter): void;
    
    typeFilter: TOperationTypeFilter;
    onTypeFilterChange(filter: TOperationTypeFilter): void;
    
    searchKeyword: string;
    onSearchChange(keyword: string): void;
    
    viewMode: TOperationViewMode;
    onViewModeChange(mode: TOperationViewMode): void;
    
    onRefresh(): void;
}

const TYPE_OPTIONS: IDropdownOption[] = [
    { key: 'all', text: 'Semua Tipe Aktivitas' },
    { key: 'ship', text: '🚢 Jadwal Kapal (Pelayaran)' },
    { key: 'loading', text: '📦 Muat Depo (Loading)' },
    { key: 'unloading', text: '🏗️ Bongkar (Unloading)' },
    { key: 'dooring', text: '🚚 Dooring / Trucking' },
];

const WorkspaceHeader: React.FC<IWorkspaceHeaderProps> = ({
    counts,
    selectedDate,
    onDateChange,
    statusFilter,
    onStatusFilterChange,
    typeFilter,
    onTypeFilterChange,
    searchKeyword,
    onSearchChange,
    viewMode,
    onViewModeChange,
    onRefresh,
}) => {
    const isToday = moment(selectedDate.startDate).isSame(moment(), 'day') && moment(selectedDate.endDate).isSame(moment(), 'day');
    const isYesterday = moment(selectedDate.startDate).isSame(moment().subtract(1, 'days'), 'day') && moment(selectedDate.endDate).isSame(moment().subtract(1, 'days'), 'day');
    const isTomorrow = moment(selectedDate.startDate).isSame(moment().add(1, 'days'), 'day') && moment(selectedDate.endDate).isSame(moment().add(1, 'days'), 'day');

    const handleQuickDate = (type: 'yesterday' | 'today' | 'tomorrow') => {
        let m = moment();
        if (type === 'yesterday') m = m.subtract(1, 'days');
        if (type === 'tomorrow') m = m.add(1, 'days');

        onDateChange(m.startOf('day').toISOString(), m.endOf('day').toISOString());
    };

    return (
        <div className={styles.headerContainer}>
            {/* KPI Metrics Summary Bar */}
            <div className={styles.kpiGrid}>
                {/* Total */}
                <div 
                    className={`${styles.kpiCard} ${statusFilter === 'all' ? styles.kpiActive : ''}`}
                    onClick={() => onStatusFilterChange('all')}
                >
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel}>Total Aktivitas</span>
                        <span className={styles.kpiValue}>{counts.total}</span>
                    </div>
                    <div className={styles.kpiIcon} style={{ background: '#f1f5f9', color: '#0284c7' }}>
                        <FontAwesomeIcon icon={faCalendarCheck} />
                    </div>
                </div>

                {/* Overdue */}
                <div 
                    className={`${styles.kpiCard} ${styles.kpiOverdue} ${statusFilter === 'overdue' ? styles.kpiActive : ''}`}
                    onClick={() => onStatusFilterChange('overdue')}
                >
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel} style={{ color: '#b91c1c' }}>Overdue / Lewat Jadwal</span>
                        <span className={styles.kpiValue} style={{ color: '#dc2626' }}>{counts.overdue}</span>
                    </div>
                    <div className={styles.kpiIcon} style={{ background: '#fee2e2', color: '#dc2626' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                </div>

                {/* In Progress */}
                <div 
                    className={`${styles.kpiCard} ${statusFilter === 'in_progress' ? styles.kpiActive : ''}`}
                    onClick={() => onStatusFilterChange('in_progress')}
                >
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel}>Sedang Berjalan</span>
                        <span className={styles.kpiValue} style={{ color: '#0284c7' }}>{counts.inProgress}</span>
                    </div>
                    <div className={styles.kpiIcon} style={{ background: '#e0f2fe', color: '#0284c7' }}>
                        <FontAwesomeIcon icon={faSpinner} />
                    </div>
                </div>

                {/* Completed */}
                <div 
                    className={`${styles.kpiCard} ${statusFilter === 'completed' ? styles.kpiActive : ''}`}
                    onClick={() => onStatusFilterChange('completed')}
                >
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel}>Selesai</span>
                        <span className={styles.kpiValue} style={{ color: '#16a34a' }}>{counts.completed}</span>
                    </div>
                    <div className={styles.kpiIcon} style={{ background: '#dcfce7', color: '#16a34a' }}>
                        <FontAwesomeIcon icon={faCalendarDays} />
                    </div>
                </div>
            </div>

            {/* Controls Bar: Date Navigation, Search, Filters, View Switcher */}
            <div className={styles.controlsBar}>
                {/* Date Quick Nav */}
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <div className={styles.dateNavGroup}>
                        <button 
                            className={`${styles.dateNavButton} ${isYesterday ? styles.dateNavActive : ''}`}
                            onClick={() => handleQuickDate('yesterday')}
                        >
                            Kemarin
                        </button>
                        <button 
                            className={`${styles.dateNavButton} ${isToday ? styles.dateNavActive : ''}`}
                            onClick={() => handleQuickDate('today')}
                        >
                            Hari Ini
                        </button>
                        <button 
                            className={`${styles.dateNavButton} ${isTomorrow ? styles.dateNavActive : ''}`}
                            onClick={() => handleQuickDate('tomorrow')}
                        >
                            Besok
                        </button>
                    </div>

                    <DatePicker
                        placeholder="Pilih Tanggal..."
                        value={moment(selectedDate.startDate).toDate()}
                        onSelectDate={(date) => {
                            if (date) {
                                const m = moment(date);
                                onDateChange(m.startOf('day').toISOString(), m.endOf('day').toISOString());
                            }
                        }}
                        formatDate={GeneralService.formatDate}
                        styles={{ root: { width: 140 } }}
                    />
                </Stack>

                {/* Search & Filters */}
                <div className={styles.filtersGroup}>
                    <div className={styles.searchBoxWrapper}>
                        <SearchBox
                            placeholder="Cari Kapal, Voy, Kontainer, Order..."
                            value={searchKeyword}
                            onChange={(_, val) => onSearchChange(val || '')}
                            onClear={() => onSearchChange('')}
                        />
                    </div>

                    <Dropdown
                        options={TYPE_OPTIONS}
                        selectedKey={typeFilter}
                        onChange={(_, opt) => {
                            if (opt) onTypeFilterChange(opt.key as TOperationTypeFilter);
                        }}
                        styles={{ root: { minWidth: 190 } }}
                    />

                    {/* View Switcher Toggle */}
                    <div className={styles.viewToggleGroup}>
                        <button
                            className={`${styles.viewToggleButton} ${viewMode === 'table' ? styles.viewToggleActive : ''}`}
                            onClick={() => onViewModeChange('table')}
                            title="Operational Table View"
                        >
                            <FontAwesomeIcon icon={faList} /> Tabel
                        </button>
                        <button
                            className={`${styles.viewToggleButton} ${viewMode === 'board' ? styles.viewToggleActive : ''}`}
                            onClick={() => onViewModeChange('board')}
                            title="Board / Card View"
                        >
                            <FontAwesomeIcon icon={faTableColumns} /> Board
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkspaceHeader;
